import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Formik, Form, Field, FormikHelpers } from 'formik'
import * as yup from 'yup'
import { ErrorResponse } from '@ocean/api'
import { usePrevious } from '@ocean/hooks'
import { Mode, Size } from '@ocean/types/dist/appearance'
import { layouts, common, controls, fields } from '@ocean/ui'
import { GoToButton } from 'components/common'
import { FinalState } from 'modules/Auth/components'
import { AnimatedSection } from 'modules/Auth/components'
import { AuthFormLayout } from 'modules/Auth/layouts'
import { namespace } from 'modules/Auth/localization'
import authRoutes from 'modules/Auth/routing/routes'
import { ForgotPasswordParams } from 'modules/Auth/types'
import {
  NoteWrapper,
  ErrorWrapper,
  AccountMessage,
  Footer,
  BackButtonWrapper,
} from './ForgotPasswordForm.styled'

const { Note, Typography, Icon } = common
const { Button, common: commonControls } = controls
const { LoadingLayout } = layouts
const { InputField } = fields
const { ErrorMessage } = commonControls

const I18_PATH = `${namespace}:auth.forgottenPassword`

type ForgotPasswordFormProps = {
  onSubmit: (formData: ForgotPasswordParams) => void
  loading?: boolean
  error?: ErrorResponse
}

export const ACCOUNT_REQUIRED_ERROR_CODE = 'ResetMyPasswordDuplicatedAccountsResponse'

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = props => {
  const { onSubmit, loading, error } = props
  const prevLoading = usePrevious(loading)
  const [formikBag, setFormikBag] = useState<FormikHelpers<ForgotPasswordParams> | null>(null)
  const [requestSuccessful, setSuccessState] = useState(false)
  const [accountRequired, setAccountRequired] = useState(false)

  useEffect(() => {
    if (prevLoading && !loading && formikBag) {
      formikBag.setSubmitting(false)

      if (error?.code === ACCOUNT_REQUIRED_ERROR_CODE) {
        setAccountRequired(true)
      }

      if (!error) {
        setSuccessState(true)
      }
    }
  }, [formikBag, loading, prevLoading, error])

  const { t } = useTranslation()

  const VALIDATION_SCHEMA: yup.SchemaOf<ForgotPasswordParams> = yup.object().shape({
    customer: yup.string().required(t(`${I18_PATH}.companyRequired`)),
    email: yup
      .string()
      .required(t(`${I18_PATH}.emailRequired`))
      .email(t(`${I18_PATH}.emailInvalid`)),
    accountId: yup.string(),
  })

  const INITIAL_VALUES: ForgotPasswordParams = {
    customer: '',
    email: '',
    accountId: '',
  }

  function handleSubmitForm(
    values: ForgotPasswordParams,
    actions: FormikHelpers<ForgotPasswordParams>
  ) {
    const { customer, email, accountId } = values
    const baseData = { customer, email }
    const formData = accountRequired ? { ...baseData, accountId } : baseData
    onSubmit(formData)
    setFormikBag(actions)
  }

  function validateAccountField(value?: string) {
    return !value ? t(`${I18_PATH}.loginIdRequired`) : undefined
  }

  function renderSuccess() {
    return (
      <FinalState
        title={t(`${I18_PATH}.requestSent`)}
        description={t(`${I18_PATH}.checkEmail`)}
        backText={t(`${I18_PATH}.backToLogin`)}
        backPath={authRoutes.login}
        dataTest='ForgotPasswordSuccess'
      />
    )
  }

  function renderForm() {
    const hasError = error?.code !== ACCOUNT_REQUIRED_ERROR_CODE
    return (
      <>
        <AnimatedSection animationDuration='0.6s'>
          <NoteWrapper>
            <Note
              title={t(`${I18_PATH}.header1`)}
              description={t(`${I18_PATH}.header2`)}
              dataTest='ForgotPasswordFormNote'
            />
          </NoteWrapper>
        </AnimatedSection>
        {hasError && (
          <ErrorWrapper>
            <ErrorMessage error={error?.error} dataTest='ForgotPasswordError' />
          </ErrorWrapper>
        )}
        <Formik
          validationSchema={VALIDATION_SCHEMA}
          initialValues={INITIAL_VALUES}
          onSubmit={handleSubmitForm}
          validateOnMount
          validateOnBlur
          validateOnChange
        >
          {({ isValid, isSubmitting }) => (
            <LoadingLayout isLoading={loading}>
              <Form>
                <AuthFormLayout>
                  <AnimatedSection animationDuration='0.7s'>
                    <Field
                      title={t(`${I18_PATH}.company`)}
                      name='customer'
                      icon={<Icon icon='company' />}
                      component={InputField}
                      size={Size.Large}
                      disabled={accountRequired}
                    />
                  </AnimatedSection>
                  <AnimatedSection animationDuration='0.8s'>
                    <Field
                      title={t(`${I18_PATH}.email`)}
                      name='email'
                      icon={<Icon icon='user' />}
                      component={InputField}
                      size={Size.Large}
                      disabled={accountRequired}
                    />
                  </AnimatedSection>
                  {accountRequired && (
                    <AnimatedSection animationDuration='0.2s'>
                      <AccountMessage>
                        <Note dataTest='ForgotPasswordFormAccountMessage'>
                          <Typography variant='small'>
                            <b>{t(`${I18_PATH}.accountSameEmailMessage`)}</b>
                          </Typography>
                        </Note>
                      </AccountMessage>
                      <Field
                        title={t(`${I18_PATH}.login`)}
                        name='accountId'
                        icon={<Icon icon='user' />}
                        component={InputField}
                        size={Size.Large}
                        validate={validateAccountField}
                      />
                    </AnimatedSection>
                  )}
                  <AnimatedSection animationDuration='0.9s'>
                    <Button
                      fill
                      type='submit'
                      title={t(`${I18_PATH}.requestNewPassword`)}
                      disabled={!isValid || isSubmitting}
                      dataTest='ForgotPasswordSubmitButton'
                    />
                  </AnimatedSection>
                </AuthFormLayout>
              </Form>
            </LoadingLayout>
          )}
        </Formik>
        <AnimatedSection animationDuration='0.9s'>
          <Footer>
            <Typography dataTest='ForgotPasswordNotice' variant='h3' color='white'>
              {t(`${I18_PATH}.footer`)}
            </Typography>
          </Footer>
        </AnimatedSection>
      </>
    )
  }

  return (
    <AnimatedSection animationDuration='0.5s'>
      <BackButtonWrapper>
        <GoToButton
          mode={Mode.Dark}
          title={t(`${I18_PATH}.backToLogin`)}
          to={authRoutes.login}
          dataTest='ForgotPasswordBackButton'
        />
      </BackButtonWrapper>
      {!requestSuccessful ? renderForm() : renderSuccess()}
    </AnimatedSection>
  )
}

export default ForgotPasswordForm
